import React from "react";
import { useFieldArray, Controller, UseFormReturn } from "react-hook-form";
import { FormField, FormService } from "@shared/services/FormServices";
import { Plus, Trash2, Copy, ChevronUp, ChevronDown } from "lucide-react";
import { SegmentedControl } from "./SegmentedControl";
import MultiSelectDropdown from "./MultiSelectDropdown";
import { MMDDPicker } from "./MMDDPicker";
import { GenericSelect } from "./Select";
import { Switch } from "@shared/ui/switch";

export interface SharedFormArrayProps {
  name: string;
  form: UseFormReturn<any>;
  itemFields: FormField[];
  variant?: "card" | "table" | "row";
  label?: string;
  helperText?: string;
  footerText?: string;
  emptyMessage?: string;
  addButtonText?: string;
  minItems?: number;
  maxItems?: number;
  className?: string;
  headerRightAction?: React.ReactNode;
  gridColumns?: number;
  rowIndexName?: string;
  renderAfterContent?: (index: number) => React.ReactNode;
  defaultItemFactory?: (fields: any[]) => any;
  gridTemplateColumns?: string;
  isDeletable?: (item: any, index: number) => boolean;
  cloneable?: boolean;
  fieldsToClearOnClone?: string[];
  onFieldChange?: (fieldName: string, value: any, index: number) => void;
  showAddButton?: boolean;
  reorderable?: boolean;
}

export const SharedFormArray: React.FC<SharedFormArrayProps> = ({
  name,
  form,
  itemFields,
  variant = "card",
  label,
  helperText,
  footerText,
  emptyMessage = "No items added yet.",
  addButtonText = "Add Item",
  minItems = 0,
  maxItems,
  className = "",
  headerRightAction,
  gridColumns,
  rowIndexName = false,
  renderAfterContent,
  defaultItemFactory,
  gridTemplateColumns,
  isDeletable,
  cloneable,
  fieldsToClearOnClone,
  onFieldChange,
  showAddButton = true,
  reorderable = false,
}) => {
  const { fields, append, remove, insert, move } = useFieldArray({
    control: form.control,
    name,
  });

  const initRef = React.useRef(false);

  React.useEffect(() => {
    if (minItems > 0 && fields.length === 0 && !initRef.current) {
      initRef.current = true;
      handleAddItem();
    }
  }, [fields.length, minItems]);

  const handleCloneItem = (index: number) => {
    if (!maxItems || fields.length < maxItems) {
      const valuesToClone = form.getValues(`${name}.${index}`);
      const newItem = { ...valuesToClone };
      if (fieldsToClearOnClone) {
        fieldsToClearOnClone.forEach(f => {
          newItem[f] = "";
        });
      }
      insert(index + 1, newItem);
    }
  };

  const handleAddItem = () => {
    if (!maxItems || fields.length < maxItems) {
      if (defaultItemFactory) {
        const currentValues = form.getValues(name) || [];
        append(defaultItemFactory(currentValues));
      } else {
        const defaultItem: any = {};
        itemFields.forEach((field) => {
          defaultItem[field.name] =
            field.defaultValue !== undefined ? field.defaultValue : "";
        });
        append(defaultItem);
      }
    }
  };

  const renderFieldInput = (
    field: FormField,
    fieldName: string,
    error: any,
    rowIndex?: number,
  ) => {
    // Resolve options dynamically per row if getOptions is provided
    const rowData =
      rowIndex !== undefined
        ? form.getValues(`${name}.${rowIndex}`)
        : undefined;
    const resolvedOptions =
      field.getOptions && rowData ? field.getOptions(rowData) : field.options;
    return (
      <Controller
        name={fieldName}
        control={form.control}
        defaultValue={field.defaultValue || ""}
        render={({ field: controllerField }) => {
          const valueStr = String(controllerField.value || "").trim();
          const isNonNumeric = valueStr !== "" && isNaN(Number(valueStr));

          const showPrefix = field.prefix && !isNonNumeric;
          const showSuffix = field.suffix && !isNonNumeric;

          const commonProps = {
            ...controllerField,
            onChange: (e: any) => {
              controllerField.onChange(e);
              const val = e?.target ? e.target.value : e;
              onFieldChange?.(field.name, val, rowIndex ?? 0);
            },
            id: fieldName,
            className: `w-full border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm py-2.5 px-3 border bg-white ${
              error ? "border-red-500" : ""
            } ${showPrefix ? "pl-8" : ""} ${showSuffix ? "pr-12" : ""}`,
            placeholder: field.placeholder,
            disabled:
              typeof field.disabled === "function"
                ? field.disabled(rowData || {})
                : field.disabled,
            readOnly:
              typeof field.readonly === "function"
                ? field.readonly(rowData || {})
                : field.readonly,
          };

          const isFieldDisabled = commonProps.disabled;
          const isFieldReadOnly = commonProps.readOnly;

          const wrapperClass = "relative flex items-center";

          if (field.customRender) {
            const handleChange = (val: any) => {
              controllerField.onChange(val);
              onFieldChange?.(field.name, val, rowIndex ?? 0);
            };
            return (field.customRender as any)(
              field,
              form,
              handleChange,
              controllerField.value
            );
          }

          if (field.type === "switch") {
            const isChecked = controllerField.value === true || controllerField.value === "true";
            return (
              <div className="flex items-center justify-center h-10">
                <Switch
                  checked={isChecked}
                  onCheckedChange={(checked) => {
                    const val = typeof controllerField.value === "boolean" ? checked : (checked ? "true" : "false");
                    controllerField.onChange(val);
                    onFieldChange?.(field.name, val, rowIndex ?? 0);
                  }}
                  disabled={isFieldDisabled || isFieldReadOnly}
                />
              </div>
            );
          }

          if (field.type === "radio-card") {
            return (
              <SegmentedControl
                options={resolvedOptions || []}
                value={controllerField.value}
                onChange={(val) => {
                  controllerField.onChange(val);
                  onFieldChange?.(field.name, val, rowIndex ?? 0);
                }}
                disabled={isFieldDisabled || isFieldReadOnly}
                fullWidth={true}
              />
            );
          }

          if (field.type === "select") {
            return (
              <GenericSelect
                value={controllerField.value}
                onChange={(val) => {
                  controllerField.onChange(val);
                  onFieldChange?.(field.name, val, rowIndex ?? 0);
                }}
                options={resolvedOptions || []}
                placeholder={
                  field.placeholder || `Select ${field.label || field.name}...`
                }
                disabled={isFieldDisabled || isFieldReadOnly}
                className={error ? "border-red-500 ring-2 ring-red-100" : ""}
                isSearchable={field.searchable !== false}
              />
            );
          }

          if (field.type === "mm-dd") {
            return (
              <MMDDPicker
                value={controllerField.value}
                onChange={(val) => {
                  controllerField.onChange(val);
                  onFieldChange?.(field.name, val, rowIndex ?? 0);
                }}
                disabled={commonProps.disabled}
                error={error}
              />
            );
          }

          if (field.type === "multi-select") {
            return (
              <MultiSelectDropdown
                options={(resolvedOptions as any) || []}
                value={
                  Array.isArray(controllerField.value)
                    ? controllerField.value
                    : controllerField.value
                    ? [controllerField.value]
                    : []
                }
                onChange={(val) => {
                  controllerField.onChange(val);
                  onFieldChange?.(field.name, val, rowIndex ?? 0);
                }}
                placeholder={
                  field.placeholder || `Select ${field.label || field.name}...`
                }
                disabled={isFieldDisabled || isFieldReadOnly}
                className={`!border-gray-300 !rounded-lg shadow-sm focus:border-blue-500 text-sm !p-[9px] ${error ? "!border-red-500" : ""}`}
                searchable={field.searchable !== false}
                clearable={field.clearable !== false}
                maxDisplayItems={field.maxDisplayItems || 3}
                closeOnSelect={field.closeOnSelect || false}
                maxSelection={field.maxSelection}
              />
            );
          }

          return (
            <div className={wrapperClass}>
              {showPrefix && (
                <span className="absolute left-3 text-gray-500 text-sm pointer-events-none">
                  {field.prefix}
                </span>
              )}
              <input
                {...commonProps}
                type={
                  field.type === "number" || field.type === "decimal"
                    ? "number"
                    : field.type === "color"
                      ? "color"
                      : field.type === "time"
                        ? "time"
                        : field.type === "date"
                          ? "date"
                          : "text"
                }
                className={
                  field.type === "color"
                    ? `w-10 h-10 p-0 border-0 rounded overflow-hidden cursor-pointer ${field.className || ""}`
                    : commonProps.className
                }
                lang={field.type === "time" ? "en-GB" : undefined}
              />
              {showSuffix && (
                <span className="absolute right-3 text-gray-500 text-sm whitespace-nowrap pointer-events-none">
                  {field.suffix}
                </span>
              )}
            </div>
          );
        }}
      />
    );
  };

  const renderCardItem = (fieldId: string, index: number) => {
    return (
      <div
        key={fieldId}
        className="border border-gray-200 rounded-xl p-5 bg-white shadow-sm relative"
      >
        <div className="absolute right-4 top-4 flex items-center gap-1">
          {cloneable && (
            <button
              type="button"
              onClick={() => handleCloneItem(index)}
              className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
              title="Clone"
            >
              <Copy className="w-4 h-4" />
            </button>
          )}
          {fields.length > minItems &&
            (!isDeletable ||
              isDeletable(form.getValues(name)?.[index], index)) && (
              <button
                type="button"
                onClick={() => remove(index)}
                className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                title="Delete"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
        </div>
        {/* We use grid to layout internal fields. Field classes like 'col-span-2' can be used. */}
        <div
          className={`grid grid-cols-1 md:grid-cols-${gridColumns || 12} gap-x-4 gap-y-5 ${cloneable ? "pr-20" : "pr-10"}`}
        >
          {itemFields.map((f) => {
            const isVisible =
              !f.showWhen ||
              f.showWhen(form.getValues(`${name}.${index}`), f.name);
            if (!isVisible) return null;

            const fieldName = `${name}.${index}.${f.name}`;
            const error = FormService.getFieldError(form, fieldName);
            return (
              <div key={f.name} className={f.className || ""}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {f.label}
                  {f.required && <span className="text-red-500 ml-1">*</span>}
                </label>
                {renderFieldInput(f, fieldName, error, index)}
                {!error && f.helperText && (
                  <p className="text-gray-500 text-xs mt-1">{f.helperText}</p>
                )}
                {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
              </div>
            );
          })}
        </div>
        {renderAfterContent && (
          <div className="mt-6 pt-6 border-t border-gray-100">
            {renderAfterContent(index)}
          </div>
        )}
      </div>
    );
  };

  const renderTable = () => {
    const isBordered = variant === "table";
    const containerClasses = isBordered
      ? "border border-gray-200 rounded-xl bg-white shadow-sm"
      : "bg-white";

    // Determine which fields are visible in at least one row, or if no rows exist, just evaluate with empty object
    const visibleFields = itemFields.filter((f) => {
      if (!f.showWhen) return true;
      if (fields.length === 0) return f.showWhen({}, f.name);
      return fields.some((_, i) =>
        f.showWhen!(form.getValues(`${name}.${i}`), f.name),
      );
    });

    const gridColsCount = gridColumns || visibleFields.length;

    return (
      <div className={containerClasses}>
        {/* Table Header */}
        <div
          className={`grid gap-4 px-4 py-3 bg-gray-50 border-b border-gray-200 ${isBordered ? "rounded-t-xl" : ""}`}
          style={{
            gridTemplateColumns:
              gridTemplateColumns ||
              `${reorderable ? "48px " : ""}${rowIndexName ? "40px " : ""}repeat(${gridColsCount}, minmax(0, 1fr)) 48px`,
          }}
        >
          {reorderable && (
            <div className="text-xs font-semibold text-gray-500 tracking-wider text-center">
              Order
            </div>
          )}
          {rowIndexName && (
            <div className="text-xs font-semibold text-gray-500 tracking-wider">
              {rowIndexName}
            </div>
          )}
          {visibleFields.map((f) => (
            <div
              key={f.name}
              className={`text-xs font-semibold text-gray-500 tracking-wider ${f.className || ""}`}
            >
              {f.label}
              {f.required && <span className="text-red-500 ml-1">*</span>}
            </div>
          ))}
          <div></div>
        </div>

        {/* Table Body */}
        <div
          className={`divide-y divide-gray-100 ${!footerText && isBordered ? "[&>div:last-child]:rounded-b-xl" : ""}`}
        >
          {fields.map((field, index) => (
            <div
              key={field.id}
              className="grid gap-4 px-4 py-3 items-start hover:bg-gray-50 transition-colors"
              style={{
                gridTemplateColumns:
                  gridTemplateColumns ||
                  `${reorderable ? "48px " : ""}${rowIndexName ? "40px " : ""}repeat(${gridColsCount}, minmax(0, 1fr)) 48px`,
              }}
            >
              {reorderable && (
                <div className="flex flex-col items-center justify-center h-10 gap-0.5">
                  <button
                    type="button"
                    disabled={index === 0}
                    onClick={() => move(index, index - 1)}
                    className="p-0.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-400"
                  >
                    <ChevronUp className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    disabled={index === fields.length - 1}
                    onClick={() => move(index, index + 1)}
                    className="p-0.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-400"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </div>
              )}
              {rowIndexName && (
                <div className="text-sm text-gray-500 font-medium flex items-center h-10">
                  {index + 1}
                </div>
              )}
              {visibleFields.map((f) => {
                const isCellVisible =
                  !f.showWhen ||
                  f.showWhen(form.getValues(`${name}.${index}`), f.name);
                if (!isCellVisible) {
                  return (
                    <div
                      key={f.name}
                      className={`flex flex-col ${f.className || ""}`}
                    ></div>
                  );
                }
                const fieldName = `${name}.${index}.${f.name}`;
                const error = FormService.getFieldError(form, fieldName);
                return (
                  <div
                    key={f.name}
                    className={`flex flex-col ${f.className || ""}`}
                  >
                    {renderFieldInput(f, fieldName, error, index)}
                    {!error && f.helperText && (
                      <p className="text-gray-500 text-xs mt-1">
                        {f.helperText}
                      </p>
                    )}
                    {error && (
                      <p className="text-red-500 text-xs mt-1">{error}</p>
                    )}
                  </div>
                );
              })}
              <div className="flex justify-center items-center h-10 gap-1">
                {cloneable && (
                  <button
                    type="button"
                    onClick={() => handleCloneItem(index)}
                    className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                    title="Clone"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                )}
                {fields.length > minItems &&
                  (!isDeletable ||
                    isDeletable(form.getValues(name)?.[index], index)) && (
                    <button
                      type="button"
                      onClick={() => remove(index)}
                      className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
              </div>
            </div>
          ))}
        </div>

        {footerText && (
          <div
            className={`px-4 py-3 bg-gray-50 text-xs text-gray-500 border-t border-gray-200 ${isBordered ? "rounded-b-xl" : ""}`}
          >
            {footerText}
          </div>
        )}
      </div>
    );
  };

  const renderRow = () => {
    // Determine which fields are visible in at least one row, or if no rows exist, just evaluate with empty object
    const visibleFields = itemFields.filter((f) => {
      if (!f.showWhen) return true;
      if (fields.length === 0) return f.showWhen({}, f.name);
      return fields.some((_, i) =>
        f.showWhen!(form.getValues(`${name}.${i}`), f.name),
      );
    });

    const gridColsCount = gridColumns || visibleFields.length;

    return (
      <div className="w-full">
        {/* Header */}
        <div
          className={`grid gap-4 pb-2`}
          style={{
            gridTemplateColumns:
              gridTemplateColumns ||
              `${rowIndexName ? "40px " : ""}repeat(${gridColsCount}, minmax(0, 1fr)) ${cloneable ? "68px" : "40px"}`,
          }}
        >
          {rowIndexName && (
            <div className="text-xs font-semibold text-gray-700">
              {rowIndexName}
            </div>
          )}
          {visibleFields.map((f) => (
            <div
              key={f.name}
              className={`text-xs font-semibold text-gray-700 ${f.className || ""}`}
            >
              {f.label}
              {f.required && <span className="text-red-500 ml-1">*</span>}
            </div>
          ))}
          <div></div>
        </div>

        {/* Rows */}
        <div className="space-y-3">
          {fields.map((field, index) => (
            <div
              key={field.id}
              className="grid gap-4 items-start"
              style={{
                gridTemplateColumns:
                  gridTemplateColumns ||
                  `${rowIndexName ? "40px " : ""}repeat(${gridColsCount}, minmax(0, 1fr)) ${cloneable ? "68px" : "40px"}`,
              }}
            >
              {rowIndexName && (
                <div className="text-sm text-gray-500 font-medium flex items-center h-10">
                  {index + 1}
                </div>
              )}
              {visibleFields.map((f) => {
                const isCellVisible =
                  !f.showWhen ||
                  f.showWhen(form.getValues(`${name}.${index}`), f.name);
                if (!isCellVisible) {
                  return (
                    <div
                      key={f.name}
                      className={`flex flex-col ${f.className || ""}`}
                    ></div>
                  );
                }
                const fieldName = `${name}.${index}.${f.name}`;
                const error = FormService.getFieldError(form, fieldName);
                return (
                  <div
                    key={f.name}
                    className={`flex flex-col ${f.className || ""}`}
                  >
                    {renderFieldInput(f, fieldName, error, index)}
                    {error && (
                      <p className="text-red-500 text-xs mt-1">{error}</p>
                    )}
                  </div>
                );
              })}
              <div className="flex justify-center items-center h-10 gap-1">
                {cloneable && (
                  <button
                    type="button"
                    onClick={() => handleCloneItem(index)}
                    className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                    title="Clone"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                )}
                {fields.length > minItems &&
                  (!isDeletable ||
                    isDeletable(form.getValues(name)?.[index], index)) && (
                    <button
                      type="button"
                      onClick={() => remove(index)}
                      className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
              </div>
            </div>
          ))}
        </div>

        {footerText && (
          <div className="mt-3 text-xs text-gray-500">{footerText}</div>
        )}
      </div>
    );
  };

  const renderEmptyState = () => {
    const [title, ...descParts] = emptyMessage.split(".");
    const desc = descParts.join(".").trim();

    return (
      <div className="border border-dashed border-gray-300 rounded-xl p-8 text-center bg-gray-50 flex flex-col items-center">
        <h4 className="text-sm font-semibold text-gray-900 mb-2">
          {title}
          {title ? "." : ""}
        </h4>
        {desc && (
          <p className="text-xs text-gray-500 max-w-md">
            {desc}
            {desc ? "." : ""}
          </p>
        )}
      </div>
    );
  };

  return (
    <div
      className={`p-6 bg-white border border-gray-200 rounded-xl space-y-4 mb-6 shadow-sm ${className}`}
    >
      {(label || helperText || headerRightAction) && (
        <div className="flex justify-between items-start mb-4">
          <div>
            {label && (
              <h3 className="text-lg font-semibold text-gray-900">{label}</h3>
            )}
            {helperText && (
              <p className="text-sm text-gray-500 mt-1">{helperText}</p>
            )}
          </div>
          {headerRightAction && <div>{headerRightAction}</div>}
        </div>
      )}

      {fields.length === 0 ? (
        renderEmptyState()
      ) : (
        <div className="space-y-4">
          {variant === "card" &&
            fields.map((field, idx) => renderCardItem(field.id, idx))}
          {variant === "table" && renderTable()}
          {variant === "row" && renderRow()}
        </div>
      )}

      {showAddButton && (!maxItems || fields.length < maxItems) && (
        <div className="flex justify-start">
          <button
            type="button"
            onClick={handleAddItem}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors shadow-sm bg-white"
          >
            <Plus className="w-4 h-4" />
            {addButtonText}
          </button>
        </div>
      )}
    </div>
  );
};
